export type OfflineMutation = {
  id: string;
  entityType: "schedules";
  entityId: string;
  data: Record<string, unknown>;
  expectedVersion: number;
  createdAt: string;
};

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("wedding-day", 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("cache"))
        request.result.createObjectStore("cache");
    };
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function saveOffline<T>(key: string, value: T): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction("cache", "readwrite");
    transaction.objectStore("cache").put(value, key);
    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function getOffline<T>(key: string): Promise<T | undefined> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction("cache", "readonly");
    const request = transaction.objectStore("cache").get(key);
    request.onsuccess = () => {
      database.close();
      resolve(request.result as T | undefined);
    };
    request.onerror = () => reject(request.error);
  });
}

const queueKey = (workspaceId: string) => `schedule-queue:${workspaceId}`;

export async function getOfflineMutations(workspaceId: string) {
  return (await getOffline<OfflineMutation[]>(queueKey(workspaceId))) ?? [];
}

export async function enqueueOfflineMutation(
  workspaceId: string,
  mutation: OfflineMutation,
) {
  const current = await getOfflineMutations(workspaceId);
  const next = [
    ...current.filter((item) => item.entityId !== mutation.entityId),
    mutation,
  ];
  await saveOffline(queueKey(workspaceId), next);
  window.dispatchEvent(
    new CustomEvent("wedding-sync-status", { detail: { pending: next.length } }),
  );
  return next;
}

export async function saveOfflineMutations(
  workspaceId: string,
  mutations: OfflineMutation[],
) {
  await saveOffline(queueKey(workspaceId), mutations);
  window.dispatchEvent(
    new CustomEvent("wedding-sync-status", {
      detail: { pending: mutations.length },
    }),
  );
}
